import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Combobox, type ComboboxOption, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api } from '@/lib/api';
import type { Company, PlatformCompanyRow } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { entityTypeLabel, mapperErrorMessage } from './mapper-ui';

/**
 * Link ONE SEED platform company record to ONE SEED EMERGE company
 * (owner 2026-10-25, M2/M4/M5). Two directions, one dialog:
 *
 * - `choose-company` (the Catalog queue): the platform record is fixed; pick
 *   the company (server-side search; a suggestion arrives preselected).
 * - `choose-platform` (a company page): the company is fixed; pick the
 *   platform record from the directory (server-side search).
 *
 * ⚠️ M5: "Also set Company name to …" is UNTICKED on every open, never
 * automatic, and shown only to a reader with `companies.update` (the server
 * refuses `adoptName` otherwise with 403 `COMPANY_NAME_NOT_PERMITTED` and
 * writes nothing). It never touches the Billing name or Xero. It is hidden
 * when the names already match.
 *
 * The PUT is an upsert keyed on the platform record: linking a record that is
 * linked elsewhere MOVES it. The dialog says so before the write.
 */

const COMPANY_PAGE_SIZE = 100;

interface NamedRef {
  id: number;
  name: string;
}

export type LinkPlatformCompanyTarget =
  | {
      mode: 'choose-company';
      platform: { platformCompanyId: string; name: string };
      /** Preselected (a suggestion), or null for an empty picker. */
      company: NamedRef | null;
    }
  | {
      mode: 'choose-platform';
      company: NamedRef;
    };

export interface LinkPlatformCompanyResult {
  row: PlatformCompanyRow;
  /** The new Company name when it was adopted, else null. */
  adoptedName: string | null;
}

export interface LinkPlatformCompanyDialogProps {
  /** Null = closed. */
  target: LinkPlatformCompanyTarget | null;
  onClose: () => void;
  onLinked: (result: LinkPlatformCompanyResult) => void;
}

function targetKey(target: LinkPlatformCompanyTarget): string {
  return target.mode === 'choose-company'
    ? `c:${target.platform.platformCompanyId}:${target.company?.id ?? ''}`
    : `p:${target.company.id}`;
}

export function LinkPlatformCompanyDialog({ target, onClose, onLinked }: LinkPlatformCompanyDialogProps) {
  if (target === null) return null;
  return <DialogBody key={targetKey(target)} target={target} onClose={onClose} onLinked={onLinked} />;
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function DialogBody({
  target,
  onClose,
  onLinked,
}: {
  target: LinkPlatformCompanyTarget;
  onClose: () => void;
  onLinked: (result: LinkPlatformCompanyResult) => void;
}) {
  const { ability } = useAuth();
  const canRename = ability.canUpdate('companies');

  const [companyId, setCompanyId] = useState<string>(
    target.mode === 'choose-company' && target.company !== null ? String(target.company.id) : '',
  );
  const [platformId, setPlatformId] = useState<string>('');
  const [adoptName, setAdoptName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── Company picker (choose-company) ── */
  const [companySearch, setCompanySearch] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadedCompanySearch, setLoadedCompanySearch] = useState<string | null>(null);
  const [companyLoadError, setCompanyLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (target.mode !== 'choose-company') return;
    const controller = new AbortController();
    api
      .listCompanies({ search: companySearch, perPage: COMPANY_PAGE_SIZE, sort: 'name', order: 'asc' }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCompanies(result.data);
        setCompanyLoadError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setCompanies([]);
        setCompanyLoadError(mapperErrorMessage(err, 'Companies could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedCompanySearch(companySearch);
      });
    return () => controller.abort();
  }, [target.mode, companySearch]);

  const companyOptions = useMemo<ComboboxOption[]>(() => {
    const options = companies.map((company) => ({ value: String(company.id), label: company.name }));
    // Keep the preselected suggestion choosable (and labelled) whatever the search returned.
    if (target.mode === 'choose-company' && target.company !== null) {
      const pre = target.company;
      if (!options.some((option) => option.value === String(pre.id))) {
        options.unshift({ value: String(pre.id), label: pre.name });
      }
    }
    return options;
  }, [companies, target]);

  /* ── Platform picker (choose-platform) ── */
  const [platformSearch, setPlatformSearch] = useState('');
  const [platformRows, setPlatformRows] = useState<PlatformCompanyRow[]>([]);
  const [loadedPlatformSearch, setLoadedPlatformSearch] = useState<string | null>(null);
  const [platformLoadError, setPlatformLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (target.mode !== 'choose-platform') return;
    const controller = new AbortController();
    api
      .listPlatformCompanies({ status: 'all', search: platformSearch }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setPlatformRows(result.data);
        setPlatformLoadError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setPlatformRows([]);
        setPlatformLoadError(mapperErrorMessage(err, 'SEED platform records could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedPlatformSearch(platformSearch);
      });
    return () => controller.abort();
  }, [target.mode, platformSearch]);

  const fixedCompanyId = target.mode === 'choose-platform' ? target.company.id : null;
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformCompanyRow | null>(null);
  const platformOptions = useMemo<ComboboxOption[]>(
    () =>
      platformRows
        .filter((row) => row.link === null || row.link.company.id !== fixedCompanyId)
        .map((row) => {
          const hint = [
            entityTypeLabel(row.entityType),
            row.countryCode,
            row.link !== null ? `Linked to ${row.link.company.name}` : null,
          ]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ');
          return hint === '' ? { value: row.platformCompanyId, label: row.name } : { value: row.platformCompanyId, label: row.name, hint };
        }),
    [platformRows, fixedCompanyId],
  );

  const searchCompanies = useCallback((term: string) => setCompanySearch(term), []);
  const searchPlatform = useCallback((term: string) => setPlatformSearch(term), []);

  /* ── What the write would do ── */
  const platformName =
    target.mode === 'choose-company' ? target.platform.name : (selectedPlatform?.name ?? null);
  const platformKey = target.mode === 'choose-company' ? target.platform.platformCompanyId : platformId;
  const chosenCompanyId =
    target.mode === 'choose-platform' ? target.company.id : companyId === '' ? null : Number.parseInt(companyId, 10);
  const chosenCompanyName =
    target.mode === 'choose-platform'
      ? target.company.name
      : (companyOptions.find((option) => option.value === companyId)?.label ?? null);

  const movesFrom =
    target.mode === 'choose-platform' && selectedPlatform !== null && selectedPlatform.link !== null
      ? selectedPlatform.link.company.name
      : null;

  const offerRename =
    canRename && platformName !== null && chosenCompanyName !== null && !sameName(platformName, chosenCompanyName);

  const ready = platformKey !== '' && chosenCompanyId !== null && Number.isInteger(chosenCompanyId) && chosenCompanyId > 0;

  const save = () => {
    if (!ready || saving || chosenCompanyId === null) return;
    const adopt = offerRename && adoptName;
    setSaving(true);
    setError(null);
    api
      .setPlatformCompanyLink(platformKey, adopt ? { companyId: chosenCompanyId, adoptName: true } : { companyId: chosenCompanyId })
      .then((row) => {
        onLinked({ row, adoptedName: adopt && platformName !== null ? platformName : null });
        onClose();
      })
      .catch((err: unknown) => {
        setError(mapperErrorMessage(err, 'The link could not be saved.'));
        setSaving(false);
      });
  };

  const pickerId = `platform-link-${target.mode}`;

  return (
    <Modal
      open
      onClose={() => {
        if (!saving) onClose();
      }}
      title="Link SEED platform record"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" className="max-sm:h-11" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="max-sm:h-11" loading={saving} disabled={!ready || saving} onClick={save}>
            Link
          </Button>
        </div>
      }
    >
      <div className="grid min-w-0 gap-4">
        <dl className="m-0 grid gap-1 text-sm">
          <dt className="text-xs text-fg-muted">SEED platform record</dt>
          <dd className="m-0 min-w-0">
            {target.mode === 'choose-company' ? (
              <span className="font-medium break-words text-fg">{target.platform.name}</span>
            ) : (
              <>
                <label htmlFor={pickerId} className="sr-only">
                  SEED platform record
                </label>
                <Combobox
                  id={pickerId}
                  value={platformId}
                  onChange={(value) => {
                    setPlatformId(value);
                    setSelectedPlatform(platformRows.find((row) => row.platformCompanyId === value) ?? null);
                    setAdoptName(false);
                  }}
                  options={platformOptions}
                  placeholder="Choose a record"
                  searchPlaceholder="Search SEED platform records…"
                  onSearch={searchPlatform}
                  loading={loadedPlatformSearch !== platformSearch}
                  disabled={saving}
                />
              </>
            )}
          </dd>
          <dt className="mt-2 text-xs text-fg-muted">SEED EMERGE company</dt>
          <dd className="m-0 min-w-0">
            {target.mode === 'choose-platform' ? (
              <span className="font-medium break-words text-fg">{target.company.name}</span>
            ) : (
              <>
                <label htmlFor={pickerId} className="sr-only">
                  SEED EMERGE company
                </label>
                <Combobox
                  id={pickerId}
                  value={companyId}
                  onChange={(value) => {
                    setCompanyId(value);
                    setAdoptName(false);
                  }}
                  options={companyOptions}
                  placeholder="Choose a company"
                  searchPlaceholder="Search companies…"
                  onSearch={searchCompanies}
                  loading={loadedCompanySearch !== companySearch}
                  disabled={saving}
                />
              </>
            )}
          </dd>
        </dl>

        {companyLoadError !== null && <Notice tone="danger">{companyLoadError}</Notice>}
        {platformLoadError !== null && <Notice tone="danger">{platformLoadError}</Notice>}

        {movesFrom !== null && (
          <Notice tone="warning">{`This record is linked to ${movesFrom}. Linking it here removes that link.`}</Notice>
        )}

        {offerRename && platformName !== null && (
          <Checkbox
            checked={adoptName}
            disabled={saving}
            onChange={(event) => setAdoptName(event.target.checked)}
            label={`Also set Company name to “${platformName}”`}
          />
        )}

        {error !== null && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
      </div>
    </Modal>
  );
}
